import { useCallback, useEffect, useState, useRef } from 'react'

import { faArrowUpRightAndArrowDownLeftFromCenter } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Image from 'next/image'
import { useIntl } from 'react-intl'
import { styled, css } from 'styled-components'
import { useMediaQuery } from 'usehooks-ts'

import {
  UnstyledButton,
  Slider,
  EmbeddedVideo,
  VideoOverlay,
  PlayButton as BasePlayButton,
} from '@b-stock/fe-core/bstock-next/components'
import {
  useVideoAutostop,
  stopVideo,
} from '@b-stock/fe-core/bstock-next/helpers'
import { FormattedMessage } from '@b-stock/fe-core/bstock-react'
import { Breakpoints, Typography } from '@b-stock/fe-core/design-system'
import { designColors } from '@b-stock/fe-core/theme'

import { getLegacyBpStaticPath } from '@/buyer-portal/buyUrlPrefix'
import { ModalGalleryWithTracking } from '@/buyer-portal/components/features/listing/media/MediaSection/ListingMedia/ModalGalleryWithTracking'
import type { MediaType } from '@/buyer-portal/components/features/listing/media/shared/types'
import type {
  NavMethod,
  OpenTrigger,
} from '@/buyer-portal/components/features/listing/media/shared/useGalleryTracking'
import { useGalleryTracking } from '@/buyer-portal/components/features/listing/media/shared/useGalleryTracking'
import { useVideoTracking } from '@/helpers/video/useVideoTracking'

const Element = styled.div`
  position: relative;
  height: 400px;

  @media ${Breakpoints.max.small} {
    height: 275px;
  }
`

const PlaceholderImg = styled.img`
  width: 100%;
  object-fit: fill;
`

const Wrapper = styled.div`
  display: flex;
  @media ${Breakpoints.max.medium} {
    flex-direction: column-reverse;
  }
`

const SliderWrapper = styled.div`
  position: relative;
  width: calc(100% - 82px);
  @media ${Breakpoints.max.medium} {
    width: 100%;
  }
`

const Pagination = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 0.5rem;
  @media ${Breakpoints.max.medium} {
    flex-direction: row;
    margin-right: 0;
    margin-top: 16px;
  }
  @media ${Breakpoints.max.small} {
    display: none;
  }
`

const PlayButton = styled(BasePlayButton)`
  width: 1.5rem;
  height: 1.5rem;

  &::after {
    border-left: 0.5rem solid ${designColors.primary.default};
    border-top: 0.3rem solid transparent;
    border-bottom: 0.3rem solid transparent;
    margin-left: 0.125rem;
  }
`

const PaginationItem = styled.div<{ $active: boolean }>`
  position: relative;
  margin-bottom: 8px;
  width: 64px;
  height: 64px;
  cursor: pointer;
  flex-shrink: 0;
  box-sizing: border-box;
  ${(o) =>
    o.$active &&
    css`
      border: 2px solid ${designColors.primary.brightBlue};
    `}

  &:hover {
    border: 2px solid ${designColors.primary.brightBlue};

    /* Lighten play button on hover */
    ${PlayButton} {
      background-color: ${designColors.neutral.white};
    }
  }

  @media ${Breakpoints.max.medium} {
    margin-bottom: 0;
    margin-right: 8px;
  }
`

const SmallImg = styled.img`
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`

const MoreImg = styled(UnstyledButton)`
  display: flex;
  align-items: center;
  text-align: center;
  width: 4rem;
  height: 4rem;
  border-radius: 0.25rem;
  cursor: pointer;
  ${Typography.Subtitle2}
  border: 0.0625rem solid ${designColors.primary.default};
  color: ${designColors.primary.default};
  padding: 0.313rem 0.5rem;
`

const SlideInfo = styled.div`
  display: flex;
  justify-content: flex-end;
  position: absolute;
  bottom: 1rem;
  right: 1rem;
`

const SlideNumber = styled.span`
  text-align: right;
  color: ${designColors.neutral.white};
  padding: 0.125rem 0.5rem;
  border-radius: 0.25rem;
  background-color: ${designColors.overlay.popup75Pct}BF;
  @media ${Breakpoints.max.small} {
    display: none;
  }
`

const MobileSlideNumber = styled(SlideNumber)`
  display: none;
  @media ${Breakpoints.max.small} {
    display: unset;
  }
`

const SeeAllBtn = styled(UnstyledButton)`
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  font-size: 1.5rem;
  color: ${designColors.neutral.white};
  background-color: rgba(20, 26, 30, 0.3);
  border-radius: 50%;
  z-index: 1;
  &:hover {
    background-color: ${designColors.neutral.veryDarkGray};
  }
  @media ${Breakpoints.max.small} {
    display: none;
  }
`

interface IListingMediaProps {
  auctionTitle: string
  media: MediaType[]
  isLoggedIn?: boolean
  onFireUnAuthCTA: (reason: string) => void
}

export const ListingMedia = ({
  auctionTitle,
  media,
  isLoggedIn = false,
  onFireUnAuthCTA,
}: IListingMediaProps) => {
  const { formatMessage } = useIntl()
  const [currentSlide, setCurrentSlide] = useState(0)
  const [galleryModalOpen, setGalleryModalOpen] = useState(false)
  const videoRefsMap = useRef<Map<number, HTMLIFrameElement>>(new Map())
  const showArrows = useMediaQuery(Breakpoints.max.medium, {
    defaultValue: false,
    initializeWithValue: false,
  })

  const { analyticsData, trackNavigate, trackOpen, trackSignInPrompt } =
    useGalleryTracking(media)

  // Autostop videos when navigating away from them
  useVideoAutostop(videoRefsMap, currentSlide, media)

  // Track video interactions in carousel
  useVideoTracking({
    videoRefsMap,
    media,
    analyticsData,
  })

  const PREVIEWS_AMOUNT = isLoggedIn ? 4 : 3

  const isOnlyPlaceholder =
    media.length === 1 && media.every((i) => i.isPlaceholder)

  const handleUnAuthCTA = useCallback(() => {
    onFireUnAuthCTA(formatMessage({ id: 'Auction.details.seeMorePhotos' }))
  }, [formatMessage, onFireUnAuthCTA])

  const navigate = useCallback(
    (newSlide: number, navMethod: NavMethod) => {
      if (newSlide === currentSlide) return
      if (!isLoggedIn && newSlide >= PREVIEWS_AMOUNT) {
        handleUnAuthCTA()
        trackSignInPrompt(newSlide, 'inline_navigate')
        return
      }
      setCurrentSlide(newSlide)
      trackNavigate(newSlide, 'inline', navMethod)
    },
    [
      PREVIEWS_AMOUNT,
      currentSlide,
      handleUnAuthCTA,
      isLoggedIn,
      trackNavigate,
      trackSignInPrompt,
    ]
  )

  const onSlideChange = useCallback(
    (cb: (index: number) => number) => navigate(cb(currentSlide), 'step'),
    [currentSlide, navigate]
  )

  const openModal = (openTrigger: OpenTrigger) => {
    if (isOnlyPlaceholder) {
      return
    }

    // Stop current video if playing
    const currentMedia = media[currentSlide]
    if (currentMedia?.type === 'video') {
      const videoIframe = videoRefsMap.current.get(currentSlide)

      if (videoIframe) {
        stopVideo(videoIframe, currentMedia.videoData.platform)
      }
    }

    if (isLoggedIn) {
      setGalleryModalOpen(true)
      trackOpen(currentSlide, openTrigger)
    } else {
      handleUnAuthCTA()
      trackSignInPrompt(currentSlide, openTrigger)
    }
  }

  const closeModalGallery = () => {
    setGalleryModalOpen(false)
  }

  useEffect(() => {
    if (!galleryModalOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModalGallery()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [galleryModalOpen])

  const [windowStart, windowEnd] = [0, PREVIEWS_AMOUNT] as const
  const relativeCount =
    windowEnd > PREVIEWS_AMOUNT
      ? media.length - windowEnd
      : media.length - PREVIEWS_AMOUNT

  const modalImages = media.map((image) => ({
    ...image,
    key: crypto.randomUUID(),
  }))

  return (
    <Wrapper>
      {media.length && !isOnlyPlaceholder ? (
        <>
          <Pagination>
            {media.slice(windowStart, windowEnd).map((img, index) => {
              const relativeIndex = windowStart + index
              const isVideo = img.type === 'video'

              return (
                <PaginationItem
                  data-testid={`media-thumbnail-${relativeIndex}`}
                  key={relativeIndex}
                  $active={relativeIndex === currentSlide}
                  onClick={() => navigate(relativeIndex, 'jump')}
                >
                  <SmallImg src={img.urls?.thumb} loading="lazy" />
                  {isVideo && (
                    <VideoOverlay>
                      <PlayButton />
                    </VideoOverlay>
                  )}
                </PaginationItem>
              )
            })}
            {media.length > PREVIEWS_AMOUNT && relativeCount >= 0 && (
              <MoreImg
                onClick={() => openModal('more_tile')}
                data-testid="more-img"
              >
                <FormattedMessage
                  id="Auction.details.images.count"
                  values={{ count: `${relativeCount}` }}
                />
              </MoreImg>
            )}
          </Pagination>

          <SliderWrapper>
            <SeeAllBtn
              onClick={() => openModal('fullscreen_button')}
              data-testid="see-all-btn"
            >
              <FontAwesomeIcon
                icon={faArrowUpRightAndArrowDownLeftFromCenter}
              />
            </SeeAllBtn>

            <Slider
              currentSlide={currentSlide}
              setCurrentSlide={onSlideChange}
              arrows={showArrows ? 'show' : 'hover'}
              loop={isLoggedIn && media.length > 1}
            >
              {media.map((img, index) => {
                const isVideo = img.type === 'video'

                return (
                  // These properties of img ought to be unique, but that is
                  // not guaranteed, so we still need to include the index as
                  // part of the key.
                  // eslint-disable-next-line react/no-array-index-key
                  <Element key={`${img.urls?.large}-${img.title}-${index}`}>
                    {isVideo ? (
                      <EmbeddedVideo
                        videoData={img.videoData}
                        title={img.title}
                        onRef={(el) => {
                          if (el) {
                            videoRefsMap.current.set(index, el)
                          }
                        }}
                      />
                    ) : (
                      <Image
                        alt="Listing image"
                        loading="lazy"
                        src={img.urls?.large || ''}
                        fill
                        style={{ objectFit: 'contain' }}
                        onClick={() => openModal('main_image')}
                      />
                    )}
                    <SlideInfo>
                      <SlideNumber>
                        <FormattedMessage
                          id="Auction.details.images.modal.count"
                          values={{
                            count: `${currentSlide + 1}`,
                            amount: `${media.length}`,
                          }}
                        />
                      </SlideNumber>
                      <MobileSlideNumber>
                        <FormattedMessage
                          id="Auction.details.images.modal.mobile.count"
                          values={{
                            count: `${currentSlide + 1}`,
                            amount: `${media.length}`,
                          }}
                        />
                      </MobileSlideNumber>
                    </SlideInfo>
                  </Element>
                )
              })}
            </Slider>
          </SliderWrapper>
          {galleryModalOpen && (
            <div data-testid="gallery-modal">
              <ModalGalleryWithTracking
                close={closeModalGallery}
                firstSlide={currentSlide}
                images={modalImages}
                title={auctionTitle}
                media={media}
              />
            </div>
          )}
        </>
      ) : (
        <PlaceholderImg
          src={getLegacyBpStaticPath('images/placeholder-image.png')}
          alt="img-placeholder"
        />
      )}
    </Wrapper>
  )
}
